/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
import { useMemo } from "react";
import moment from "moment";
import currency from "currency.js";
import { Align } from "../../../../components/shared/Table/style";
import { ITableColumn } from "../../../../interfaces/Table/TableColumn";
import { ButtonThemeEnum } from "../../../../components/Buttons/styles";
import {
  GetRfqs200ResponseBlocksInner,
  GetRfqs200ResponseBlocksInnerLegsInner,
  SideResponse,
} from "../../../../codegen-api";
import Tag from "../../../../components/ConfirmationModal/Tag";
import {
  BACKGROUND_COLORS,
  TEXT_COLORS,
} from "../../../../constants/design/colors";
import {
  BlockId,
  ChevronWrapper,
  OfferButton,
  TagWrapper,
} from "./style";
import { getTimeAgo, nanosToSeconds } from "../../../../utils/date";
import { Chevron } from "../../../../components/shared/Chevron/style";
import { BestAskCell, BestBidCell } from "./cells";

export interface BuildRFQColumnsParams {
  t: any;
  expandedRows: number[];
  generateLegTag: (
    leg: GetRfqs200ResponseBlocksInnerLegsInner
  ) => Array<string | JSX.Element>;
  quoteByBlock: (blockId: string, side: SideResponse) => any;
  onOffer: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    block: GetRfqs200ResponseBlocksInner,
    side: SideResponse
  ) => void;
  onCancelQuote: (quoteId: string) => Promise<void>;
  getHighestPrecision: (
    legs: GetRfqs200ResponseBlocksInnerLegsInner[]
  ) => number;
  onCancelBlock: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    block: GetRfqs200ResponseBlocksInner
  ) => Promise<void>;
  translateAll: any;
}

export function buildRFQColumns({
  t,
  expandedRows,
  generateLegTag,
  quoteByBlock,
  onOffer,
  onCancelQuote,
  getHighestPrecision,
  onCancelBlock,
  translateAll,
}: BuildRFQColumnsParams): ITableColumn<GetRfqs200ResponseBlocksInner>[] {
  return [
    {
      title: t("id"),
      id: "block_id",
      accessor: "block_id",
      align: "center",
      Cell: ({ value, row }) => (
        <Align align="center">
          <BlockId $isTaker={row.original.role === "taker"}>
            {value.slice(-6).toUpperCase()}
          </BlockId>
        </Align>
      ),
    },
    {
      title: t("legs"),
      id: "legs",
      accessor: "legs",
      Cell: ({ value, row }) => {
        const isExpanded =
          expandedRows.includes(row.index) || (value || []).length === 1;
        const tags = useMemo(() => {
          if (value && value.length > 0) {
            const tag: Array<Array<string | JSX.Element>> = [
              generateLegTag(value[0]),
            ];

            if (!isExpanded && value.length > 1) {
              tag.push([`+${value.length - 1} more`]);
            } else if (isExpanded) {
              value.forEach((leg, i) => {
                if (i !== 0) tag.push(generateLegTag(leg));
              });
            }

            return tag.map((texts, i) => (
              <Tag
                key={i}
                color={TEXT_COLORS.two}
                backgroundColor={BACKGROUND_COLORS.component}
                texts={texts}
              />
            ));
          }

          return undefined;
        }, [isExpanded, value]);

        return (
          <TagWrapper isExpanded={isExpanded}>
            {(value || []).length > 1 ? (
              <ChevronWrapper>
                <Chevron direction={isExpanded ? "up" : "down"} />
              </ChevronWrapper>
            ) : null}
            {tags}
          </TagWrapper>
        );
      },
    },
    {
      title: t("block_size"),
      id: "block_size",
      accessor: "amount",
      align: "left",
      Cell: ({ value }) => <Align align="left">{value}</Align>,
    },
    {
      title: " ",
      id: "sell_cta",
      accessor: "orderbook",
      align: "center",
      Cell: ({ value, row }) => {
        const quote = quoteByBlock(row.original.block_id, SideResponse.Buy);
        return (
          <Align align="center">
            {row.original.role === "taker" ? (
              <OfferButton
                onClick={(e) => onOffer(e, row.original, SideResponse.Buy)}
                buttonTheme={ButtonThemeEnum.HIGHLIGHT}
                disabled={(value?.bids || []).length === 0}
              >
                Accept
              </OfferButton>
            ) : quote ? (
              <OfferButton
                onClick={() => onCancelQuote(quote.quote_id)}
                buttonTheme={ButtonThemeEnum.SYSTEM}
              >
                Cancel Bid
              </OfferButton>
            ) : (
              <OfferButton
                onClick={(e) => onOffer(e, row.original, SideResponse.Buy)}
                buttonTheme={ButtonThemeEnum.POSITIVE}
              >
                Bid
              </OfferButton>
            )}
          </Align>
        );
      },
    },
    {
      title: t("best_bid"),
      id: "best_bid",
      accessor: "orderbook",
      align: "right",
      Cell: ({ value, row }) => (
        <BestBidCell value={value} row={row} quoteByBlock={quoteByBlock} />
      ),
    },
    {
      title: t("mark_price"),
      id: "mark_price",
      accessor: "mark_price",
      align: "right",
      Cell: ({ value, row }) => {
        const highestPrecision = getHighestPrecision(row.original.legs || []);

        return (
          <Align align="right">
            {currency(value, { precision: highestPrecision }).format()}
          </Align>
        );
      },
    },
    {
      title: t("best_ask"),
      id: "best_ask",
      accessor: "orderbook",
      align: "right",
      Cell: ({ value, row }) => (
        <BestAskCell value={value} row={row} quoteByBlock={quoteByBlock} />
      ),
    },
    {
      title: " ",
      id: "buy_cta",
      accessor: "orderbook",
      align: "center",
      Cell: ({ value, row }) => {
        const quote = quoteByBlock(row.original.block_id, SideResponse.Sell);
        return (
          <Align align="center">
            {row.original.role === "taker" ? (
              <OfferButton
                onClick={(e) => onOffer(e, row.original, SideResponse.Sell)}
                buttonTheme={ButtonThemeEnum.HIGHLIGHT}
                disabled={(value?.asks || []).length === 0}
              >
                Accept
              </OfferButton>
            ) : quote ? (
              <OfferButton
                onClick={() => onCancelQuote(quote.quote_id)}
                buttonTheme={ButtonThemeEnum.SYSTEM}
              >
                Cancel Ask
              </OfferButton>
            ) : (
              <OfferButton
                onClick={(e) => onOffer(e, row.original, SideResponse.Sell)}
                buttonTheme={ButtonThemeEnum.NEGATIVE}
              >
                Ask
              </OfferButton>
            )}
          </Align>
        );
      },
    },
    {
      title: " ",
      id: "cancel_cta",
      accessor: "role",
      align: "center",
      Cell: ({ value, row }: any) => (
        <Align align="center">
          {value === "taker" ? (
            <OfferButton
              onClick={(e) => onCancelBlock(e, row.original)}
              disabled={value !== "taker"}
              buttonTheme={ButtonThemeEnum.SYSTEM}
            >
              Cancel RFQ
            </OfferButton>
          ) : null}
        </Align>
      ),
    },
    {
      title: t("date_time"),
      id: "date_time",
      accessor: "created_timestamp",
      align: "right",
      Cell: ({ value }) => (
        <Align align="right">
          {getTimeAgo(moment.unix(nanosToSeconds(value)), translateAll)}
        </Align>
      ),
    },
  ];
}
